'use client';

import { useCookieConsent } from "@/app/contexts/cookie-consent-context";
import MapViewWithConsent from "@/app/__to_be_integrated/_____components/map/MapViewWithConsent";
import ConsentReopener from "@/app/__to_be_integrated/_____components/cookie-consent/components/consent-reopener";
import { ComponentRef, forwardRef } from "react";
import { Box } from "@mui/material";
import { Map, MapProps } from "@vis.gl/react-google-maps";
import Image from "next/image";

export type ConsentFallbackVerticalPosition = "top" | "center" | "bottom";
export type ConsentFallbackHorizontalPosition = "left" | "center" | "right";

export interface MapViewProps extends MapProps {
    consentPosition?: {
        vertical?: ConsentFallbackVerticalPosition;
        horizontal?: ConsentFallbackHorizontalPosition;
    };
}

function toVerticalAnchorPercent(position: ConsentFallbackVerticalPosition): number {
    switch (position) {
        case "top":
            return 0;
        case "bottom":
            return 100;
        case "center":
        default:
            return 50;
    }
}

function toHorizontalAnchorPercent(position: ConsentFallbackHorizontalPosition): number {
    switch (position) {
        case "left":
            return 25;
        case "right":
            return 75;
        case "center":
        default:
            return 50;
    }
}

const MapView = forwardRef<ComponentRef<typeof Map>, MapViewProps>(
    ({ consentPosition, ...props }, ref) => {
        const { consent } = useCookieConsent();
        const verticalPosition = consentPosition?.vertical ?? "center";
        const horizontalPosition = consentPosition?.horizontal ?? "center";
        const verticalAnchorPercent = toVerticalAnchorPercent(verticalPosition);
        const horizontalAnchorPercent = toHorizontalAnchorPercent(horizontalPosition);

        if (consent.externalMedia) {
            return <MapViewWithConsent ref={ref} {...props} />;
        } else {
            return (
                <Box
                    className="cookie-consent-map-view relative flex flex-auto size-full p-4"
                    sx={{
                        backgroundColor: 'var(--foreground)'
                    }}
                >
                    <Image src="/assets/map-background.png" alt="" fill objectFit="cover" style={{ opacity: 0.5 }} />
                    <Box
                        sx={{
                            position: "absolute",
                            top: `${verticalAnchorPercent}%`,
                            left: `${horizontalAnchorPercent}%`,
                            transform: `translate(-50%, -${verticalAnchorPercent}%)`,
                            width: "min(90%, 28rem)",
                            maxWidth: "100%",
                        }}
                    >
                        <ConsentReopener type="externalMedia" className="max-w-none" />
                    </Box>
                </Box>
            );
        }
    }
)

MapView.displayName = 'MapView';
export default MapView;
